import { Component, effect, ElementRef, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { LayoutType } from '@app/model/enums/layout-type.enum';
import { SlideBuilderSection } from '@app/model/slide-builder-section';
import { BackgroundSizeOption, HorizontalPositionOption, MediaTypeOption, SlideBuilderSectionColumn, VerticalPositionOption } from 'library-explorer';

@Component({
    selector: 'app-slide-builder-section-background',
    templateUrl: './slide-builder-section-background.component.html',
    styleUrls: ['./slide-builder-section-background.component.scss'],
    standalone: false
})
export class SlideBuilderSectionBackgroundComponent {
  @Input() public isFirstSection = false;
  @Input() public isLastSection = false;

  @Input() public set section(value: SlideBuilderSection) {
    this._section = value;
    if (value) {
      this.init();
    }
  }
  
  public get section() {
    return this._section;
  }

  @Input() public set leftColumn(value: ElementRef) {
    this._leftColumn = value;
    this.setColumnsHeightForMobile();
  }

  public get leftColumn() {
    return this._leftColumn;
  }

  @Input() public set rightColumn(value: ElementRef) {
    this._rightColumn = value;
    this.setColumnsHeightForMobile();
  }

  public get rightColumn() {
    return this._rightColumn;
  }

  public mediaRatio = signal('');
  public backgroundSize = signal(BackgroundSizeOption.AUTO);

  @Output() public mediaRatioChanged = new EventEmitter<string>();

  public leftColumnWidth: number;
  public rightColumnWidth: number;

  public leftColumnHeight: number;
  public rightColumnHeight: number;
  
  public leftColumnBackground: Partial<SlideBuilderSection>;
  public rightColumnBackground: Partial<SlideBuilderSection>;

  public readonly layoutTypes: typeof LayoutType = LayoutType;
  public readonly backgroundSizeTypes: typeof BackgroundSizeOption = BackgroundSizeOption;
  public readonly verticalPositionTypes: typeof VerticalPositionOption = VerticalPositionOption;
  public readonly horizontalPositionTypes: typeof HorizontalPositionOption = HorizontalPositionOption;
  public readonly columns: typeof SlideBuilderSectionColumn = SlideBuilderSectionColumn;
  public readonly mediaType: typeof MediaTypeOption = MediaTypeOption;

  private readonly defaultColumnWidth = 50;

  private _section: SlideBuilderSection;

  private _leftColumn: ElementRef;
  private _rightColumn: ElementRef;

  private mediaQuery = window.matchMedia('(min-width: 768px)');

  private ratios = {
    [MediaTypeOption.IMAGE]: null,
    [MediaTypeOption.VIDEO]: null
  };

  constructor(private readonly elementRef: ElementRef) {
    effect(() => {
      const aspectRatio = this.backgroundSize() === BackgroundSizeOption.FULL ? this.mediaRatio() : '';
      this.mediaRatioChanged.emit(aspectRatio);
    });
  }

  public mediaRatioUpdated(ratio: string, column = SlideBuilderSectionColumn.SINGLE, mediaType = MediaTypeOption.IMAGE): void {
    if (column !== SlideBuilderSectionColumn.SINGLE) {
      return;
    }

  
    this.ratios[mediaType] = ratio;
    const { [MediaTypeOption.IMAGE]: imageRatio, [MediaTypeOption.VIDEO]: videoRatio } = this.ratios;
    this.mediaRatio.set(videoRatio ?? imageRatio);
  }

  private init(): void {
    const observer = new ResizeObserver(() => {
      this.setBackgroundSize();
      this.setColumnsRatios();
    });
    
    observer.observe(this.elementRef.nativeElement);

    this.setBackgroundSize();
    this.setColumnsRatios();
  }

  private setBackgroundSize(): void {
    this.backgroundSize.set(this.section.backgroundSize);
  }

  private setColumnsRatios(): void {
    const ratio = this.section.columnsRatio || 1;

    const { left: leftPadding = 32, right: rightPadding = 32 } = this.section.spacing || {};

    const columnGap = 32;
    const sidePadding = leftPadding + rightPadding;
    const containerWidth = this.elementRef.nativeElement.offsetWidth - sidePadding - columnGap;
    const adjustedRatio = this.defaultColumnWidth * ratio / 100;

    this.leftColumnWidth = containerWidth * adjustedRatio + leftPadding + columnGap / 2;
    this.rightColumnWidth = containerWidth * (1 - adjustedRatio) + rightPadding + columnGap / 2;

    this.leftColumnBackground = {
      enableBackground: this.section?.enableBackground,
      ...this.section.leftBackground
    };

    this.rightColumnBackground = {
      enableBackground: this.section?.enableBackground,
      ...this.section.rightBackground
    };
  }

  private setColumnsHeightForMobile(): void {
    if (!this.leftColumn || !this.rightColumn) {
      return;
    }

    const observer = new ResizeObserver(() => {
      const sectionHeight = this.elementRef.nativeElement.parentElement.offsetHeight;
      const [leftColumnHeight, rightColumHeight] = [this.leftColumn?.nativeElement?.offsetHeight, this.rightColumn?.nativeElement?.offsetHeight];
      const columnsHeight = leftColumnHeight + rightColumHeight;

      this.leftColumnHeight = leftColumnHeight + (sectionHeight - columnsHeight) / 2;
      this.rightColumnHeight = rightColumHeight + (sectionHeight - columnsHeight) / 2;
    });
    
    observer.observe(this._leftColumn.nativeElement);
    observer.observe(this._rightColumn.nativeElement);
  }
}
